import { normalizeFieldNames } from '../../../events/UserEventReducer';

export const GROUND_LOOT_ALLOWED_ACTIONS = new Set(['IDLE', 'MOVING', 'RESTING']);

function valueFrom(source, camelName, pascalName) {
  return source?.[camelName] ?? source?.[pascalName];
}

export function createGroundLootOperationId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function canUseGroundLoot(action) {
  return GROUND_LOOT_ALLOWED_ACTIONS.has(String(action || 'IDLE').toUpperCase());
}

export function isNearGroundLoot(playerPosition, target) {
  if (!playerPosition || !target) return false;
  return Math.abs(Number(playerPosition.x) - Number(target.x))
    + Math.abs(Number(playerPosition.y) - Number(target.y)) <= 1;
}

export function isDropSourceUnchanged(inventory, request) {
  const source = (Array.isArray(inventory) ? inventory : []).find(item => (
    Number(item.x) === Number(request?.item?.x)
    && Number(item.y) === Number(request?.item?.y)
    && item.itemId === request?.item?.itemId
    && String(item.itemInstanceId || '')
      === String(request?.item?.itemInstanceId || '')
  ));
  return Boolean(source)
    && Number(source.quantity) === Number(request?.item?.quantity);
}

export function containsGroundLootEntry(detail, entryId) {
  return Boolean(entryId) && (detail?.entries || []).some(
    entry => entry.entryId === entryId,
  );
}

export function groundLootSummarySignature(containerId, summary) {
  return summary
    ? [containerId, summary.revision, summary.slotCount].join(':')
    : `${containerId}:removed`;
}

export function normalizeGroundLootSummary(summary) {
  if (!summary) return null;
  const x = Number(valueFrom(summary, 'x', 'X'));
  const y = Number(valueFrom(summary, 'y', 'Y'));
  if (!Number.isInteger(x) || !Number.isInteger(y)) return null;
  return {
    containerId: valueFrom(summary, 'containerId', 'ContainerId'),
    x,
    y,
    tier: String(valueFrom(summary, 'tier', 'Tier') || 'small').toLowerCase(),
    slotCount: Number(valueFrom(summary, 'slotCount', 'SlotCount') ?? 0),
    capacity: Number(valueFrom(summary, 'capacity', 'Capacity') ?? 5),
    earliestExpiresAtUtc: valueFrom(
      summary,
      'earliestExpiresAtUtc',
      'EarliestExpiresAtUtc',
    ) ?? valueFrom(summary, 'nextExpiryUtc', 'NextExpiryUtc') ?? null,
    expiresAtUtc: valueFrom(summary, 'expiresAtUtc', 'ExpiresAtUtc') || null,
    revision: Number(valueFrom(summary, 'revision', 'Revision') ?? 0),
  };
}

export function normalizeWorldLootSummary(summary) {
  if (!summary) return null;
  const containerId = valueFrom(
    summary,
    'containerId',
    'ContainerId',
  );
  const x = Number(valueFrom(summary, 'x', 'X'));
  const y = Number(valueFrom(summary, 'y', 'Y'));
  if (!containerId || !Number.isInteger(x) || !Number.isInteger(y)) {
    return null;
  }
  const status = String(
    valueFrom(summary, 'status', 'Status') || 'filled',
  ).toLowerCase();
  return {
    containerId: String(containerId),
    typeKey: String(valueFrom(summary, 'typeKey', 'TypeKey') || ''),
    name: String(
      valueFrom(summary, 'name', 'Name') || 'Loot Container',
    ),
    x,
    y,
    status,
    slotCount: Math.max(
      0,
      Number(valueFrom(summary, 'slotCount', 'SlotCount') || 0),
    ),
    capacity: Math.max(
      1,
      Number(valueFrom(summary, 'capacity', 'Capacity') || 1),
    ),
    closedImageId: String(valueFrom(
      summary,
      'closedImageId',
      'ClosedImageId',
    ) || ''),
    emptyImageId: String(valueFrom(
      summary,
      'emptyImageId',
      'EmptyImageId',
    ) || ''),
    renderScaleTiles: Math.min(
      3,
      Math.max(
        0.5,
        Number(valueFrom(
          summary,
          'renderScaleTiles',
          'RenderScaleTiles',
        ) || 1),
      ),
    ),
    refillAtUtc: valueFrom(
      summary,
      'refillAtUtc',
      'RefillAtUtc',
    ) || null,
    openedAtUtc: valueFrom(
      summary,
      'openedAtUtc',
      'OpenedAtUtc',
    ) || null,
    openUntilUtc: valueFrom(
      summary,
      'openUntilUtc',
      'OpenUntilUtc',
    ) || null,
    canDeposit: Boolean(valueFrom(
      summary,
      'canDeposit',
      'CanDeposit',
    )),
    revision: Number(valueFrom(summary, 'revision', 'Revision') || 0),
    authored: true,
  };
}

export function normalizeResourceNodeSummary(summary) {
  if (!summary) return null;
  const placementId = valueFrom(summary, 'placementId', 'PlacementId')
    ?? valueFrom(summary, 'nodeId', 'NodeId')
    ?? valueFrom(summary, 'id', 'Id');
  const resourceKey = valueFrom(
    summary,
    'resourceKey',
    'ResourceKey',
  );
  const x = Number(valueFrom(summary, 'x', 'X'));
  const y = Number(valueFrom(summary, 'y', 'Y'));
  if (
    !placementId
    || !resourceKey
    || !Number.isInteger(x)
    || !Number.isInteger(y)
  ) {
    return null;
  }
  const rawStatus = String(
    valueFrom(summary, 'status', 'Status') || 'available',
  ).toLowerCase();
  return {
    placementId: String(placementId),
    resourceKey: String(resourceKey),
    yieldItemId: String(
      valueFrom(summary, 'yieldItemId', 'YieldItemId') || '',
    ),
    itemName: String(
      valueFrom(summary, 'itemName', 'ItemName') || '',
    ),
    mapImageId: String(
      valueFrom(summary, 'mapImageId', 'MapImageId') || '',
    ),
    collectionDurationSeconds: Math.max(
      0,
      Number(valueFrom(
        summary,
        'collectionDurationSeconds',
        'CollectionDurationSeconds',
      )) || 0,
    ),
    x,
    y,
    status: ['available', 'reserved', 'depleted'].includes(rawStatus)
      ? rawStatus
      : 'reserved',
    remainingCharges: Math.max(
      0,
      Number(valueFrom(
        summary,
        'remainingCharges',
        'RemainingCharges',
      )) || 0,
    ),
    respawnAtUtc: valueFrom(summary, 'respawnAtUtc', 'RespawnAtUtc') || null,
    revision: Number(valueFrom(summary, 'revision', 'Revision') || 0),
  };
}

export function normalizeChampionSummary(summary) {
  if (!summary) return null;
  const placementId = valueFrom(
    summary,
    'placementId',
    'PlacementId',
  );
  const enemyId = valueFrom(summary, 'enemyId', 'EnemyId');
  const x = Number(valueFrom(summary, 'x', 'X'));
  const y = Number(valueFrom(summary, 'y', 'Y'));
  if (
    !placementId
    || !enemyId
    || !Number.isInteger(x)
    || !Number.isInteger(y)
  ) {
    return null;
  }
  return {
    placementId: String(placementId),
    enemyId: String(enemyId),
    name: String(
      valueFrom(summary, 'name', 'Name') || 'Region Champion',
    ),
    imageId: String(valueFrom(summary, 'imageId', 'ImageId') || ''),
    level: Math.max(
      1,
      Number(valueFrom(summary, 'level', 'Level')) || 1,
    ),
    kind: valueFrom(summary, 'kind', 'Kind') === 'boss'
      ? 'boss'
      : 'champion',
    isAggressive: Boolean(valueFrom(
      summary,
      'isAggressive',
      'IsAggressive',
    )),
    x,
    y,
  };
}

export function normalizeRoamingEnemySummary(summary) {
  if (!summary) return null;
  const instanceId = valueFrom(summary, 'instanceId', 'InstanceId');
  const enemyId = valueFrom(summary, 'enemyId', 'EnemyId');
  const x = Number(valueFrom(summary, 'x', 'X'));
  const y = Number(valueFrom(summary, 'y', 'Y'));
  if (
    !instanceId
    || !enemyId
    || !Number.isInteger(x)
    || !Number.isInteger(y)
  ) return null;
  return {
    instanceId: String(instanceId),
    enemyId: String(enemyId),
    name: String(valueFrom(summary, 'name', 'Name') || 'Enemy'),
    imageId: String(valueFrom(summary, 'imageId', 'ImageId') || ''),
    x,
    y,
    level: Math.max(1, Number(valueFrom(summary, 'level', 'Level')) || 1),
    isAggressive: Boolean(valueFrom(
      summary,
      'isAggressive',
      'IsAggressive',
    )),
    awarenessRangeTiles: Math.max(
      0,
      Number(valueFrom(
        summary,
        'awarenessRangeTiles',
        'AwarenessRangeTiles',
      )) || 0,
    ),
    pursuitIntervalSeconds: Math.max(
      1,
      Number(valueFrom(
        summary,
        'pursuitIntervalSeconds',
        'PursuitIntervalSeconds',
      )) || 1,
    ),
    isPursuingViewer: Boolean(valueFrom(
      summary,
      'isPursuingViewer',
      'IsPursuingViewer',
    )),
  };
}

export function normalizeOnlinePlayerPresence(summary) {
  if (!summary) return null;
  const name = String(valueFrom(summary, 'name', 'Name') || '').trim();
  const x = Number(valueFrom(summary, 'x', 'X'));
  const y = Number(valueFrom(summary, 'y', 'Y'));
  if (!name || !Number.isInteger(x) || !Number.isInteger(y)) return null;
  const population = Number(valueFrom(
    summary,
    'tilePopulation',
    'TilePopulation',
  ));
  const avatarSelection = valueFrom(
    summary,
    'avatarSelection',
    'AvatarSelection',
  );
  return {
    name,
    x,
    y,
    currentAction: String(valueFrom(
      summary,
      'currentAction',
      'CurrentAction',
    ) || 'IDLE').toUpperCase(),
    avatarSelection: avatarSelection
      ? normalizeFieldNames(avatarSelection)
      : null,
    tilePopulation: Number.isFinite(population)
      ? Math.max(1, Math.floor(population))
      : 1,
  };
}

export function normalizeRegionWorldSnapshot(
  payload,
  fallbackRegionId = null,
  fallbackServerTimeOffsetMs = 0,
  fallbackWorldInstanceId = 'shared',
) {
  const groundLootPayload = valueFrom(payload, 'groundLoot', 'GroundLoot');
  const groundLoot = (
    Array.isArray(groundLootPayload)
      ? groundLootPayload
      : Array.isArray(payload) ? payload : []
  )
    .map(normalizeGroundLootSummary)
    .filter(summary => summary?.containerId);
  const generatedAtValue = valueFrom(
    payload,
    'generatedAtMs',
    'GeneratedAtMs',
  );
  const resourceNodesPayload = valueFrom(
    payload,
    'resourceNodes',
    'ResourceNodes',
  );
  const onlinePlayersPayload = valueFrom(
    payload,
    'onlinePlayers',
    'OnlinePlayers',
  );
  const roamingEnemiesPayload = valueFrom(
    payload,
    'roamingEnemies',
    'RoamingEnemies',
  );
  const worldLootPayload = valueFrom(
    payload,
    'worldLootContainers',
    'WorldLootContainers',
  );
  const generatedAtMs = Number(generatedAtValue ?? Date.now());
  const championPayload = valueFrom(payload, 'champion', 'Champion');
  return {
    messageType: 'RegionWorldSnapshot',
    regionId: valueFrom(payload, 'regionId', 'RegionId') ?? fallbackRegionId,
    worldInstanceId: String(
      valueFrom(payload, 'worldInstanceId', 'WorldInstanceId')
        || fallbackWorldInstanceId,
    ),
    generatedAtMs,
    serverTimeOffsetMs: generatedAtValue == null
      ? fallbackServerTimeOffsetMs
      : generatedAtMs - Date.now(),
    groundLoot,
    worldLootContainers: (Array.isArray(worldLootPayload)
      ? worldLootPayload
      : [])
      .map(normalizeWorldLootSummary)
      .filter(Boolean),
    resourceNodes: (Array.isArray(resourceNodesPayload)
      ? resourceNodesPayload
      : [])
      .map(normalizeResourceNodeSummary)
      .filter(Boolean),
    roamingEnemies: (Array.isArray(roamingEnemiesPayload)
      ? roamingEnemiesPayload
      : [])
      .map(normalizeRoamingEnemySummary)
      .filter(Boolean),
    champion: normalizeChampionSummary(championPayload),
    onlinePlayers: (Array.isArray(onlinePlayersPayload)
      ? onlinePlayersPayload
      : [])
      .map(normalizeOnlinePlayerPresence)
      .filter(Boolean),
  };
}

export function normalizeGroundLootEntry(entry) {
  if (!entry) return null;
  const slotIndex = Number(
    valueFrom(entry, 'slotIndex', 'SlotIndex')
    ?? valueFrom(entry, 'slot', 'Slot')
    ?? 0,
  );
  return {
    entryId: valueFrom(entry, 'entryId', 'EntryId'),
    slotIndex,
    itemId: valueFrom(entry, 'itemId', 'ItemId'),
    itemInstanceId: valueFrom(entry, 'itemInstanceId', 'ItemInstanceId') || '',
    quantity: Number(valueFrom(entry, 'quantity', 'Quantity') ?? 0),
    droppedAtUtc: valueFrom(entry, 'droppedAtUtc', 'DroppedAtUtc') || null,
    lootableAtUtc: valueFrom(entry, 'lootableAtUtc', 'LootableAtUtc')
      ?? valueFrom(entry, 'ownerGraceUntilUtc', 'OwnerGraceUntilUtc')
      ?? null,
    expiresAtUtc: valueFrom(entry, 'expiresAtUtc', 'ExpiresAtUtc') || null,
    canTake: Boolean(valueFrom(entry, 'canTake', 'CanTake')),
    depositedByUserId: String(valueFrom(
      entry,
      'depositedByUserId',
      'DepositedByUserId',
    ) || ''),
  };
}

export function normalizeGroundLootDetail(payload) {
  const containerPayload = valueFrom(payload, 'container', 'Container') ?? payload;
  const entriesPayload = valueFrom(payload, 'entries', 'Entries');
  const container = normalizeGroundLootSummary(containerPayload);
  return {
    container,
    entries: (Array.isArray(entriesPayload) ? entriesPayload : [])
      .map(normalizeGroundLootEntry)
      .filter(entry => entry?.entryId && entry.itemId && entry.quantity > 0),
  };
}

export function normalizeWorldLootDetail(payload) {
  const containerPayload = valueFrom(
    payload,
    'container',
    'Container',
  ) ?? payload;
  const entriesPayload = valueFrom(payload, 'entries', 'Entries');
  return {
    container: normalizeWorldLootSummary(containerPayload),
    entries: (Array.isArray(entriesPayload) ? entriesPayload : [])
      .map(normalizeGroundLootEntry)
      .filter(entry => (
        entry?.entryId
        && entry.itemId
        && entry.quantity > 0
      )),
  };
}

export function normalizeStarterCacheSummary(payload) {
  if (!payload) return null;
  const cachePayload = valueFrom(payload, 'cache', 'Cache')
    ?? valueFrom(payload, 'starterCache', 'StarterCache')
    ?? payload;
  const cacheId = valueFrom(cachePayload, 'cacheId', 'CacheId')
    ?? valueFrom(cachePayload, 'id', 'Id')
    ?? valueFrom(cachePayload, 'containerId', 'ContainerId');
  const x = Number(
    valueFrom(cachePayload, 'x', 'X')
    ?? valueFrom(cachePayload, 'positionX', 'PositionX'),
  );
  const y = Number(
    valueFrom(cachePayload, 'y', 'Y')
    ?? valueFrom(cachePayload, 'positionY', 'PositionY'),
  );
  const entries = valueFrom(payload, 'entries', 'Entries')
    ?? valueFrom(cachePayload, 'entries', 'Entries');
  const untakenEntries = Array.isArray(entries)
    ? entries.filter(entry => !Boolean(valueFrom(entry, 'taken', 'Taken')))
    : null;
  const slotCount = Number(
    untakenEntries
      ? untakenEntries.length
      : valueFrom(cachePayload, 'slotCount', 'SlotCount') ?? 0,
  );
  const requiredEntryCount = Number(
    valueFrom(cachePayload, 'requiredEntryCount', 'RequiredEntryCount')
    ?? (
      untakenEntries
        ? untakenEntries.filter(entry => Boolean(
          valueFrom(entry, 'requiredToStart', 'RequiredToStart'),
        )).length
        : slotCount
    ),
  );
  if (!cacheId || !Number.isInteger(x) || !Number.isInteger(y) || slotCount <= 0) {
    return null;
  }
  return {
    cacheId: String(cacheId),
    containerId: String(cacheId),
    x,
    y,
    tier: 'small',
    slotCount,
    requiredEntryCount,
    capacity: Math.max(
      5,
      Number(valueFrom(cachePayload, 'capacity', 'Capacity') ?? slotCount),
    ),
    revision: Number(valueFrom(cachePayload, 'revision', 'Revision') ?? 0),
    runId: valueFrom(cachePayload, 'runId', 'RunId') ?? null,
    private: true,
  };
}

export function normalizeStarterCacheDetail(payload) {
  const container = normalizeStarterCacheSummary(payload);
  const cachePayload = valueFrom(payload, 'cache', 'Cache')
    ?? valueFrom(payload, 'starterCache', 'StarterCache')
    ?? payload;
  const entriesPayload = valueFrom(payload, 'entries', 'Entries')
    ?? valueFrom(cachePayload, 'entries', 'Entries');
  const entries = (Array.isArray(entriesPayload) ? entriesPayload : [])
    .map((entry, index) => {
      const entryId = valueFrom(entry, 'entryId', 'EntryId')
        ?? valueFrom(entry, 'id', 'Id');
      const itemId = valueFrom(entry, 'itemId', 'ItemId');
      const quantity = Number(valueFrom(entry, 'quantity', 'Quantity') ?? 1);
      const canTakeValue = valueFrom(entry, 'canTake', 'CanTake');
      return {
        entryId: entryId ? String(entryId) : '',
        slotIndex: Number(
          valueFrom(entry, 'slotIndex', 'SlotIndex')
          ?? valueFrom(entry, 'slot', 'Slot')
          ?? index,
        ),
        itemId,
        itemInstanceId: valueFrom(
          entry,
          'itemInstanceId',
          'ItemInstanceId',
        ) || '',
        quantity,
        canTake: canTakeValue === undefined ? true : Boolean(canTakeValue),
        requiredToStart: Boolean(
          valueFrom(entry, 'requiredToStart', 'RequiredToStart'),
        ),
        taken: Boolean(valueFrom(entry, 'taken', 'Taken')),
        expiresAtUtc: null,
        lootableAtUtc: null,
      };
    })
    .filter(entry => (
      !entry.taken
      && entry.entryId
      && entry.itemId
      && entry.quantity > 0
    ));
  return { container, entries };
}

const GROUND_LOOT_ERROR_MESSAGES = {
  already_claimed: 'Another player picked up that item first.',
  container_full: 'This treasure pack is full. Choose another tile.',
  entry_expired: 'That item has disappeared.',
  expired: 'That item has disappeared.',
  inventory_full: 'Your Pack has no room for that item.',
  invalid_action: 'You cannot use ground treasure during your current action.',
  invalid_state: 'You cannot use ground treasure during your current action.',
  drop_compensated: 'The interrupted drop was returned to your Pack. Try again.',
  not_near: 'Move onto or next to the treasure pack first.',
  out_of_range: 'Move onto or next to the treasure pack first.',
  owner_grace: 'Only the player who dropped this item can take it right now.',
  operation_expired: 'The interrupted transfer expired. Try again.',
  starter_item_locked: 'Equip required starter gear before dropping it.',
  take_released: 'The interrupted loot claim was released. Try again.',
};

export function groundLootErrorCode(error) {
  return String(
    error?.details?.code
    ?? error?.details?.Code
    ?? error?.details?.error?.code
    ?? error?.details?.error?.Code
    ?? '',
  ).toLowerCase();
}

export function isTerminalGroundLootDropError(error) {
  return new Set([
    'drop_compensated',
    'idempotency_conflict',
    'operation_expired',
  ]).has(groundLootErrorCode(error));
}

export function isTerminalGroundLootTakeError(error) {
  return new Set([
    'already_claimed',
    'idempotency_conflict',
    'take_released',
  ]).has(groundLootErrorCode(error));
}

export function groundLootErrorMessage(error, fallback) {
  return GROUND_LOOT_ERROR_MESSAGES[groundLootErrorCode(error)]
    || error?.message
    || fallback;
}
